import type { LineChartSeries } from "@mantine/charts";
import { Group, Stack, Title } from "@mantine/core";
import { DateTime } from "luxon";
import { numericFormatter } from "react-number-format";
import { NoChartDataWrapper } from "../../../../mantine/charts/components/NoChartDataWrapper/NoChartDataWrapper";
import { DATE_TIME_DISPLAY_WITHOUT_SECONDS_FORMAT } from "../../../../utils/dates/dates.constants";
import { MantineLineChart } from "../../../Charts/LineChart/MantineLineChart";
import { getColoredSeries } from "../../../Charts/utils/getColoredSeries";
import { TotalsSideBar } from "../../../RevenueMonitoring/TotalsSideBar/TotalsSideBar";

const UNIT = " kWh";

const SERIES = [
  { name: "redispatch", label: "Redispatch" },
  { name: "dva", label: "DV-Abregelung" }
] as const satisfies Array<LineChartSeries>;

const coloredSeries = getColoredSeries(SERIES);

export interface AusfallarbeitChartDataPoint {
  date: number;
  redispatch: number | null;
  dva: number | null;
}

export interface AusfallarbeitChartProps {
  data: AusfallarbeitChartDataPoint[];
  isLoading?: boolean;
}

function AusfallarbeitChart({
  data,
  isLoading = false
}: AusfallarbeitChartProps) {
  const sums = data.reduce(
    (acc, item) => ({
      redispatch: acc.redispatch + (item.redispatch ?? 0),
      dva: acc.dva + (item.dva ?? 0)
    }),
    {
      redispatch: 0,
      dva: 0
    }
  );

  const totals = {
    redispatch: data.some((item) => item.redispatch !== null)
      ? sums.redispatch
      : null,
    dva: data.some((item) => item.dva !== null) ? sums.dva : null
  };

  return (
    <Stack gap="xs">
      <Title order={5}>Ausfallarbeiten in kWh</Title>
      <Group align="center" wrap="nowrap">
        <NoChartDataWrapper isLoading={isLoading} noData={data.length === 0}>
          <MantineLineChart
            data={data}
            dataKey="date"
            h={450}
            legendPosition="top"
            series={SERIES}
            withLegend
            xAxisProps={{
              type: "number",
              domain: ["dataMin", "dataMax"],
              tickFormatter: (ts: number) =>
                DateTime.fromMillis(ts).toFormat(
                  DATE_TIME_DISPLAY_WITHOUT_SECONDS_FORMAT
                )
            }}
            yAxisLabel="Ausfallarbeit"
            yAxisProps={{
              tickFormatter: (value: number) =>
                numericFormatter((value / 1000).toString(), {
                  decimalScale: 1,
                  decimalSeparator: ",",
                  suffix: "k",
                  thousandSeparator: ".",
                  fixedDecimalScale: true
                })
            }}
          />
        </NoChartDataWrapper>
        <TotalsSideBar
          mah={450}
          totalsSeries={coloredSeries.map((series) => ({
            label: series.label,
            value: totals[series.name],
            color: series.color
          }))}
          unit={UNIT}
        />
      </Group>
    </Stack>
  );
}

export { AusfallarbeitChart };
